import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import BrandLogo from '../components/layout/BrandLogo';
import { motion, AnimatePresence } from 'framer-motion';

const { 
  FiPlay, FiCheckCircle, FiAlertCircle, FiLoader, FiDatabase, 
  FiLayers, FiArrowRight, FiUser, FiCalendar, FiMapPin,
  FiHelpCircle, FiInfo, FiSmartphone, FiZap, FiChevronDown, FiChevronUp
} = FiIcons;

const DEMO_STEPS = [
  { id: 1, title: 'Sync Profiles', desc: 'Import kid school IDs and extracurricular schedules automatically.', icon: FiUser },
  { id: 2, title: 'Generate Wall', desc: 'ParentPad creates a visual "Command Wall" based on your weekly load.', icon: FiLayers },
  { id: 3, title: 'Smart Pickups', desc: 'AI coordinates who is picking up which kid based on traffic and work calls.', icon: FiMapPin },
  { id: 4, title: 'Meal Alignment', desc: 'Syncs grocery lists with practice schedules so dinner is never late.', icon: FiCalendar },
  { id: 5, title: 'Review & Optimize', desc: 'End-of-week insights show where your family "found time" for rest.', icon: FiCheckCircle },
];

const SAMPLE_RECORDS = [
  { id: 'EV-001', name: 'Leo\'s Soccer Practice', type: 'Sports', time: '4:30 PM', status: 'Confirmed', priority: 'High' },
  { id: 'EV-002', name: 'Mia\'s Dentist Appt', type: 'Health', time: '10:00 AM', status: 'Pending', priority: 'Medium' },
  { id: 'EV-003', name: 'PTA Bake Sale', type: 'School', time: '3:00 PM', status: 'Draft', priority: 'Low' },
  { id: 'EV-004', name: 'Family Pizza Night', type: 'Social', time: '6:30 PM', status: 'Confirmed', priority: 'Medium' },
];

const FAQS = [
  {
    q: "How do I set up my family profile?",
    a: "Setup is instant. Simply add your family members and optionally connect your school's parent portal. ParentPad will automatically scrape the calendar for school holidays and early dismissals."
  },
  {
    q: "Is the data in this demo real?",
    a: "No, all data shown in this Demo mode is simulated for privacy-safe exploration. No personal family details are collected or stored in this demo."
  },
  {
    q: "How does the AI handle pickup coordination?",
    a: "Our engine looks at both parents' work calendars and real-time traffic. If Dad has a meeting running late, ParentPad automatically notifies Mom and updates the wall."
  },
  {
    q: "What about privacy and security?",
    a: "ParentPad uses local-first encryption. Your family's schedule and locations are encrypted end-to-end, meaning only you can see the data."
  },
  {
    q: "Are there different pricing plans?",
    a: "ParentPad offers a 'Free Forever' tier for basic scheduling. Our 'Pro' plan includes AI pickup automation and dedicated school portal sync."
  }
];

export default function DemoView() {
  const [activeStep, setActiveStep] = useState(1);
  const [simulatedState, setSimulatedState] = useState('success');
  const [expandedFaq, setExpandedFaq] = useState(null);

  return (
    <div className="max-w-5xl mx-auto space-y-12 pb-24 px-2">
      
      {/* 1. Overview Section */}
      <section className="bg-slate-900 rounded-[3rem] p-8 md:p-16 text-white shadow-2xl relative overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1542037104857-ffbb0b9155f2?w=1200&q=80" 
          alt="Family Kitchen" 
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a365d] to-transparent"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <BrandLogo size="sm" color="light" />
            <div className="h-4 w-px bg-white/20 mx-2"></div>
            <h2 className="text-xs font-black uppercase tracking-[0.2em] text-yellow-300">Product Overview</h2>
          </div>
          <h1 className="text-4xl md:text-6xl font-black mb-8 leading-[1.1] tracking-tighter">
            Stop Managing Logistics. <br />
            <span className="text-yellow-300 underline decoration-white/20 underline-offset-8">Start Raising Humans.</span>
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 text-blue-50/80">
            <p className="text-xl leading-relaxed font-medium">
              ParentPad is the centralized "Command Wall" for busy parents. We solve the chaos of fragmented school apps, scattered threads, and "who-is-picking-up-whom" confusion.
            </p>
            <div className="grid grid-cols-2 gap-6">
              <div className="p-4 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm">
                <SafeIcon icon={FiZap} className="text-yellow-300 text-2xl mb-2" />
                <h4 className="text-white font-bold text-sm mb-1 uppercase tracking-widest">Speed</h4>
                <p className="text-[10px] leading-tight">Syncs 10+ school apps in under 30 seconds.</p>
              </div>
              <div className="p-4 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm">
                <SafeIcon icon={FiSmartphone} className="text-yellow-300 text-2xl mb-2" />
                <h4 className="text-white font-bold text-sm mb-1 uppercase tracking-widest">Mobile</h4>
                <p className="text-[10px] leading-tight">Native iOS & Android apps for on-the-go sync.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Demo / How It Works Section */}
      <section className="space-y-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#1a365d] text-yellow-300 rounded-2xl flex items-center justify-center shadow-lg">
              <SafeIcon icon={FiPlay} className="text-xl" />
            </div>
            <h2 className="text-3xl font-black text-[#1a365d] tracking-tight">How It Works</h2>
          </div>
          <p className="text-slate-500 font-bold uppercase tracking-widest text-[10px]">Step {activeStep} of 5</p>
        </div>
        
        <div className="bg-white rounded-[2.5rem] p-6 md:p-10 shadow-xl shadow-slate-200/50 border border-slate-100">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4 space-y-4">
              {DEMO_STEPS.map((step) => (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={`w-full text-left p-5 rounded-3xl border-2 transition-all flex items-start gap-5 ${
                    activeStep === step.id 
                    ? 'bg-[#1a365d] text-white border-[#1a365d] shadow-2xl scale-[1.05] z-10' 
                    : 'bg-slate-50 text-slate-600 border-slate-100 hover:border-slate-300'
                  }`}
                >
                  <div className={`mt-1 p-3 rounded-2xl flex-shrink-0 ${activeStep === step.id ? 'bg-yellow-300 text-[#1a365d]' : 'bg-white text-slate-400 shadow-sm'}`}>
                    <SafeIcon icon={step.icon} className="text-xl" />
                  </div>
                  <div>
                    <h4 className="font-black text-sm md:text-base tracking-tight">{step.title}</h4>
                    <p className={`text-[10px] md:text-xs mt-1.5 leading-relaxed font-medium ${activeStep === step.id ? 'text-blue-100' : 'text-slate-400'}`}>
                      {step.desc}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            <div className="lg:col-span-8 bg-slate-100/50 rounded-[2.5rem] p-10 border-2 border-white flex flex-col items-center justify-center min-h-[450px] relative overflow-hidden">
               <div className="absolute inset-0 bg-grid-slate-200 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-0"></div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="text-center max-w-md relative z-10"
                >
                  <div className="w-24 h-24 bg-yellow-300 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-2xl rotate-6 text-[#1a365d] text-4xl">
                    <SafeIcon icon={DEMO_STEPS.find(s => s.id === activeStep).icon} />
                  </div>
                  <h4 className="text-3xl font-black text-[#1a365d] mb-4 tracking-tighter">
                    {DEMO_STEPS.find(s => s.id === activeStep).title}
                  </h4>
                  <p className="text-slate-600 leading-relaxed text-lg font-medium">
                    {activeStep === 1 ? "Start by connecting your digital life. We sync with Google, iCloud, and niche school portals like Brightwheel or Bloomz." :
                     activeStep === 2 ? "The Command Wall is your family's home screen. It prioritizes what's happening NOW and what's coming in the next 4 hours." :
                     activeStep === 3 ? "GPS-aware logistics. If a parent is stuck in traffic, ParentPad pings the other parent or a trusted backup automatically." :
                     activeStep === 4 ? "Dinner decisions made easy. We suggest meals based on the time you actually have between practice and bedtime." :
                     "The 'Rest Gap' metric. We show you exactly how much free time you gained back by automating your family's logistical chores."}
                  </p>
                  <button 
                    onClick={() => setActiveStep(prev => prev < 5 ? prev + 1 : 1)}
                    className="mt-10 bg-[#1a365d] text-white px-10 py-4 rounded-2xl font-black flex items-center gap-3 mx-auto hover:bg-blue-900 transition-all shadow-xl active:scale-95"
                  >
                    {activeStep === 5 ? 'Finish Demo' : 'Keep Going'}
                    {activeStep < 5 && <SafeIcon icon={FiArrowRight} />}
                  </button>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Sample Data Section */}
      <section className="space-y-8">
        <div className="flex items-center gap-3 px-4">
          <div className="w-12 h-12 bg-white text-[#1a365d] border border-slate-200 rounded-2xl flex items-center justify-center shadow-sm">
            <SafeIcon icon={FiDatabase} className="text-xl" />
          </div>
          <h2 className="text-3xl font-black text-[#1a365d] tracking-tight">Sample Data</h2>
        </div>
        
        <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-slate-200/40 border border-slate-100 overflow-hidden">
          <div className="p-6 bg-[#1a365d] text-white flex justify-between items-center">
            <div className="flex items-center gap-3">
               <div className="w-8 h-8 bg-yellow-300 rounded-lg flex items-center justify-center text-[#1a365d]">
                  <SafeIcon icon={FiCalendar} />
               </div>
               <span className="text-sm font-black uppercase tracking-widest">Weekly Load Insight</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-[10px] font-black uppercase tracking-tighter">Real-time Feed</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Event / Activity</th>
                  <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Type</th>
                  <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Time</th>
                  <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {SAMPLE_RECORDS.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50/80 transition-colors group">
                    <td className="px-8 py-6">
                      <div className="font-black text-slate-800 tracking-tight group-hover:text-[#1a365d] transition-colors">{row.name}</div>
                      <div className="text-[10px] text-slate-400 font-bold">{row.id}</div>
                    </td>
                    <td className="px-8 py-6">
                      <span className="text-xs font-bold text-slate-600 bg-slate-100 px-3 py-1 rounded-lg">{row.type}</span>
                    </td>
                    <td className="px-8 py-6 text-sm font-black text-slate-800">{row.time}</td>
                    <td className="px-8 py-6">
                      <span className={`text-[10px] font-black px-3 py-1.5 rounded-full border-2 tracking-widest uppercase ${
                        row.status === 'Confirmed' ? 'bg-green-50 text-green-700 border-green-100' :
                        row.status === 'Pending' ? 'bg-yellow-50 text-yellow-700 border-yellow-100' :
                        'bg-slate-50 text-slate-500 border-slate-200'
                      }`}>
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 4. Simulated States Section */}
      <section className="space-y-8">
        <div className="flex items-center gap-3 px-4">
          <div className="w-12 h-12 bg-yellow-300 text-[#1a365d] rounded-2xl flex items-center justify-center shadow-lg">
            <SafeIcon icon={FiAlertCircle} className="text-xl" />
          </div>
          <h2 className="text-3xl font-black text-[#1a365d] tracking-tight">Simulated UI States</h2>
        </div>

        <div className="bg-white rounded-[2.5rem] p-6 md:p-12 shadow-xl border border-slate-100">
          <div className="flex flex-wrap gap-3 mb-12 bg-slate-100 p-2 rounded-[1.5rem] w-fit mx-auto lg:mx-0">
            {['success', 'loading', 'empty', 'error'].map(state => (
              <button
                key={state}
                onClick={() => setSimulatedState(state)}
                className={`px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  simulatedState === state 
                  ? 'bg-white text-[#1a365d] shadow-xl scale-[1.05]' 
                  : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {state}
              </button>
            ))}
          </div>

          <div className="bg-slate-50/50 rounded-[2rem] p-16 border-2 border-dashed border-slate-200 min-h-[350px] flex items-center justify-center text-center relative overflow-hidden">
            <AnimatePresence mode="wait">
              {/* States content remains the same but with enhanced styling */}
              {simulatedState === 'loading' && (
                <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
                  <div className="w-16 h-16 border-4 border-[#1a365d]/10 border-t-[#1a365d] rounded-full animate-spin mx-auto shadow-xl"></div>
                  <p className="text-slate-500 font-black text-xs uppercase tracking-widest">Optimizing Family Flow...</p>
                </motion.div>
              )}
              {simulatedState === 'success' && (
                <motion.div key="success" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="space-y-6">
                  <div className="w-24 h-24 bg-green-100 text-green-600 rounded-[2rem] flex items-center justify-center mx-auto text-4xl shadow-lg rotate-3">
                    <SafeIcon icon={FiCheckCircle} />
                  </div>
                  <div>
                    <h4 className="text-3xl font-black text-[#1a365d] tracking-tighter">All Systems Sync'd</h4>
                    <p className="text-slate-500 font-medium mt-2">12 school events and 4 practices added to the wall.</p>
                  </div>
                </motion.div>
              )}
              {simulatedState === 'empty' && (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
                  <div className="w-24 h-24 bg-slate-200 text-slate-400 rounded-[2rem] flex items-center justify-center mx-auto text-4xl">
                    <SafeIcon icon={FiLayers} />
                  </div>
                  <div>
                    <h4 className="text-3xl font-black text-slate-700 tracking-tighter">Quiet Weekend</h4>
                    <p className="text-slate-500 font-medium mt-2">No activities detected. Time for some rest?</p>
                  </div>
                </motion.div>
              )}
              {simulatedState === 'error' && (
                <motion.div key="error" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="bg-red-50 p-10 rounded-[2.5rem] border-2 border-red-100 max-w-sm space-y-6 shadow-2xl">
                  <SafeIcon icon={FiAlertCircle} className="text-red-500 text-5xl mx-auto" />
                  <h4 className="text-2xl font-black text-red-900 tracking-tighter uppercase">Connection Lost</h4>
                  <p className="text-sm text-red-600 leading-relaxed font-bold">The School District Portal is currently down for maintenance. We'll retry automatically in 10 minutes.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* 5. FAQ Section */}
      <section className="space-y-8">
        <div className="flex items-center gap-3 px-4">
          <div className="w-12 h-12 bg-white text-[#1a365d] border border-slate-200 rounded-2xl flex items-center justify-center shadow-sm">
            <SafeIcon icon={FiHelpCircle} className="text-xl" />
          </div>
          <h2 className="text-3xl font-black text-[#1a365d] tracking-tight">FAQ</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FAQS.map((faq, idx) => (
            <div key={idx} className="bg-white rounded-3xl border-2 border-slate-100 overflow-hidden transition-all hover:border-yellow-300 group">
              <button 
                onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                className="w-full flex items-center justify-between p-6 text-left"
              >
                <span className="font-black text-slate-800 pr-4 leading-tight group-hover:text-[#1a365d] transition-colors">{faq.q}</span>
                <SafeIcon 
                  icon={expandedFaq === idx ? FiChevronUp : FiChevronDown} 
                  className={`text-slate-400 transition-transform ${expandedFaq === idx ? 'text-[#1a365d]' : ''}`} 
                />
              </button>
              <AnimatePresence>
                {expandedFaq === idx && (
                  <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 pt-0 text-slate-500 text-sm leading-relaxed font-medium bg-slate-50/50">
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </section>

      {/* Brand Footer */}
      <footer className="pt-20 pb-10 flex flex-col items-center gap-6 border-t border-slate-200">
        <BrandLogo size="md" color="dark" />
        <div className="flex flex-wrap justify-center gap-8 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
          <a href="#" className="hover:text-[#1a365d] transition-colors">Privacy</a>
          <a href="#" className="hover:text-[#1a365d] transition-colors">Terms</a>
          <a href="#" className="hover:text-[#1a365d] transition-colors">Support</a>
          <a href="#" className="hover:text-[#1a365d] transition-colors">Press</a>
        </div>
        <p className="text-slate-400 text-[10px] font-bold tracking-widest uppercase mt-4">
          Made by LingLink AI and DigLab AI
        </p>
      </footer>
    </div>
  );
}